/**
 * Pure data-transformation helpers for the Contacts / People feature.
 * No side-effects, no React, no API calls.
 * Lives in lib/utils alongside other app-wide utilities.
 */

import { hp } from "@/lib/utils";

// ─── Generic ────────────────────────────────────────────────────────────────

export const safeArray = (value) => (Array.isArray(value) ? value : []);

// ─── Response parsers ───────────────────────────────────────────────────────

/**
 * Normalise a /groups API response into a flat array of
 * { uuid, name, personCount } objects.
 */
export const getGroupsFromResponse = (response) => {
  const raw =
    response && response.data && Array.isArray(response.data.data)
      ? response.data.data
      : response && Array.isArray(response.data)
      ? response.data
      : [];

  return safeArray(raw).map((group) => ({
    uuid: group.uuid,
    name: group.name,
    personCount: Number(group.person_count || 0),
  }));
};

/**
 * Normalise a /persons API response into { persons, total }.
 */
export const getPeoplePayload = (response) => {
  const data = response && response.data ? response.data : {};
  const persons =
    safeArray(data.persons).length > 0
      ? safeArray(data.persons)
      : safeArray(data.data && data.data.persons);

  const total =
    Number(data.total) ||
    Number(data.totalPeople) ||
    Number(data.numberOfPeople) ||
    Number(data.count) ||
    persons.length;

  return { persons, total };
};

// ─── Row mapper ─────────────────────────────────────────────────────────────

/**
 * Map a raw person API record to a flat table row shape.
 */
export const toRow = (person) => {
  const source = person && person.data ? person.data : {};
  const firstName = source["First Name"] || source["First Name "] || "";
  const lastName = source["Last Name"] || source["Last Name "] || "";
  const gps = source["GPS Location"] || {};
  const gpsString =
    gps && (gps.latitude || gps.longitude)
      ? `${gps.latitude || ""}${gps.latitude && gps.longitude ? "," : ""}${gps.longitude || ""}`
      : "";

  return {
    key: person.uuid,
    uuid: person.uuid,
    name: `${firstName} ${lastName}`.trim() || "-",
    phone: source["Phone Number"] || "-",
    email: source.Email || "-",
    occupation: source.Occupation || "-",
    idNumber: source["Id Number"] || "-",
    gender: source.Gender || "-",
    title: source.Title || "-",
    country: source.Country || "-",
    city: source.City || "-",
    dob: source["Date of Birth"] || "-",
    gps: gpsString || "-",
    raw: person,
  };
};

// ─── Form → API payload ─────────────────────────────────────────────────────

/**
 * Convert Add/Edit person form values into the shape the API expects.
 */
export function personToPayload(values) {
  var userId = "";
  try {
    var userCookie = hp.getCookie("user");
    var user = userCookie ? JSON.parse(userCookie) : {};
    userId = user.user_id || user.account_id || user.uuid || user.id || "";
  } catch (e) {
    userId = "";
  }

  var gps = {};
  if (values.gpsLatitude !== undefined && values.gpsLatitude !== null && values.gpsLatitude !== "") {
    gps.latitude = Number(values.gpsLatitude);
  }
  if (values.gpsLongitude !== undefined && values.gpsLongitude !== null && values.gpsLongitude !== "") {
    gps.longitude = Number(values.gpsLongitude);
  }

  var dob =
    values.dateOfBirth && values.dateOfBirth.format
      ? values.dateOfBirth.format("YYYY-MM-DD")
      : undefined;

  var personData = {
    "First Name": values.firstName || undefined,
    "Last Name": values.lastName || undefined,
    "Phone Number": values.phoneNumber || undefined,
    Email: values.email || undefined,
    Occupation: values.occupation || undefined,
    "Id Number": values.idNumber || undefined,
    Gender: values.gender || undefined,
    Title: values.title || undefined,
    Country: values.country || undefined,
    City: values.city || undefined,
    "Address 1": values.address1 || undefined,
    "Address 2": values.address2 || undefined,
    "Date of Birth": dob,
    "GPS Location": gps,
    Custom: {},
  };

  return {
    personData: Object.keys(personData).reduce(function (acc, key) {
      var val = personData[key];
      if (key === "GPS Location" || key === "Custom") {
        acc[key] = val;
      } else if (val !== undefined && val !== null && val !== "") {
        acc[key] = val;
      }
      return acc;
    }, {}),
    personMeta: { source: "dfs people" },
    ...(userId ? { userId } : {}),
  };
}
